import React, { useState, useEffect, useCallback } from 'react';
import ContentCard from '../components/ContentCard';
import StoriesComponent from '../components/Stories';
import GiftPurchaseInterface from '../components/GiftPurchaseInterface'; // Заглушка для интерфейса подарков
// LoadingScreen больше не используется здесь
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { useData } from '../context/DataContext'; // Импортируем useData
import './HomePage.css';

const log = (message) => {
  console.log(`[HomePage] ${message}`);
};

const HomePage = () => {
  log('Инициализация/рендер HomePage');
  
  const { user } = useAuth();
  const { settings } = useSettings();
  
  // Получаем данные и состояния из DataContext
  const {
    content, isContentLoading, contentError, fetchContentData, // contentLoaded, // Закомментировано, т.к. ESLint ругается на неиспользование
    tags, areTagsLoading, tagsError, // fetchTagsData, // Закомментировано
    // tagsLoaded, // Закомментировано
    // stories, // Закомментировано
    areStoriesLoading, // storiesError, // Закомментировано
    // refreshAllData // ESLint: 'refreshAllData' is assigned a value but never used. Было: refreshAllData
  } = useData();

  const [selectedTag, setSelectedTag] = useState('all');
  const [isGiftModeActive, setIsGiftModeActive] = useState(false); // Новое состояние для режима подарков
  // userPurchases остается локальным, так как специфичен для отображения купленного контента пользователем
  const [userPurchases, setUserPurchases] = useState([]);
  // Удаляем состояния и функции, связанные с модальным окном подарка на ContentCard,
  // так как теперь есть отдельный режим/страница для подарков.
  // const [showGiftInterfaceModal, setShowGiftInterfaceModal] = useState(false); 
  // const [selectedContentForGift, setSelectedContentForGift] = useState(null);

  // Загрузка покупок пользователя (остается локальной)
  const fetchUserPurchases = useCallback(async () => {
    if (user) {
      log('Начинаем загрузку покупок пользователя');
      try {
        const telegramId = user?.telegramId || localStorage.getItem('telegram_id');
        const response = await fetch('/api/payments/content-access', {
          headers: { 'X-Telegram-ID': telegramId, 'Content-Type': 'application/json' }
        });
        if (response.ok) {
          const data = await response.json();
          // Новый эндпоинт возвращает { contentWithAccess: [...] }
          setUserPurchases(data.contentWithAccess || []);
        } else {
          log(`Ошибка при запросе покупок: ${response.status}`);
        }
      } catch (err) {
        log(`Ошибка при загрузке покупок пользователя: ${err.message}`);
        setUserPurchases([]);
      }
    }
  }, [user]);

  useEffect(() => {
    fetchUserPurchases();
    const purchaseCheckInterval = setInterval(fetchUserPurchases, 30000);
    return () => clearInterval(purchaseCheckInterval);
  }, [user, fetchUserPurchases]);

  // Проверка статуса платежа (остается локальной)
  useEffect(() => {
    const checkPaymentStatus = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const paymentId = urlParams.get('payment_id');
      if (paymentId) {
        try {
          const newUrl = window.location.pathname;
          window.history.replaceState({}, document.title, newUrl);
          const response = await fetch(`/api/payments/status/${paymentId}`);
          if (response.ok) {
            const data = await response.json();
            if (data.status === 'completed') fetchUserPurchases();
          }
        } catch (err) {
          console.error('Ошибка при проверке статуса платежа:', err);
        }
      }
    };
    checkPaymentStatus();
  }, [fetchUserPurchases]);

  // Загрузка основного контента и тегов при монтировании HomePage, если они еще не загружены
  // DataContext сам управляет флагами *_loaded
  useEffect(() => {
    log('HomePage mounted. Requesting initial content and tags from DataContext if not loaded.');
    // Функции в DataContext (fetchContentData, fetchTagsData) уже проверяют, были ли данные загружены
    // и не будут делать повторный запрос без forceReload=true.
    // При первой загрузке HomePage (и DataProvider) эти функции вызовутся из useEffect в DataProvider.
    // При последующих монтированиях HomePage (например, при навигации), если данные уже в контексте,
    // повторной загрузки не будет, если только мы не передадим forceReload=true.
    // Если мы хотим, чтобы при каждом заходе на HomePage данные обновлялись, можно вызвать с forceReload:
    // fetchContentData('all', true);
    // fetchTagsData(true);
    // Но для кэширования лучше оставить как есть, DataProvider уже загрузил при инициализации.
    // Если пользователь меняется, DataProvider сам перезагрузит все.
  }, []); // Пустой массив зависимостей, т.к. DataProvider управляет первоначальной загрузкой

  // Загрузка контента при изменении выбранного тега
  useEffect(() => {
    if (selectedTag) {
      log(`Selected tag changed to: ${selectedTag}. Fetching content for this tag.`);
      // DataContext.fetchContentData будет вызван с новым тегом.
      // Он сам решит, нужно ли грузить (если тег не 'all' или если 'all', но contentLoaded=false)
      fetchContentData(selectedTag);
    }
  }, [selectedTag, fetchContentData]);


  const handleTagClick = (tagId) => {
    // При клике на любой тег (включая "Все"), выключаем режим подарков
    setIsGiftModeActive(false); 
    log(`Tag clicked: ${tagId}. Gift mode deactivated.`);

    if (selectedTag === tagId) {
      // Если кликнули на уже активный тег
      if (tagId === 'all') {
        log('Forcing reload for "all" tag as it was already selected.');
        fetchContentData('all', true); // Принудительно перезагружаем "Все"
      } else {
        // Если это другой активный обычный тег, сбрасываем на "Все"
        // (повторный клик на активный тег, кроме "Все", сбрасывает на "Все")
        setSelectedTag('all'); 
      }
    } else {
      // Кликнули на новый неактивный тег
      setSelectedTag(tagId); 
    }
    // fetchContentData будет вызван из useEffect для selectedTag, если selectedTag изменился
  };

  const handleGiftButtonClick = () => {
    log('Gift button clicked. Activating gift mode.');
    setIsGiftModeActive(true);
    // Можно также сбросить selectedTag на 'all', чтобы при выходе из режима подарков отображались все посты
    // setSelectedTag('all'); 
    // Или оставить selectedTag как есть, чтобы вернуться к последнему выбранному тегу
  };

  // Общее состояние загрузки для LoadingScreen
  // Временно упрощаем для отладки, чтобы сосредоточиться на загрузке историй
  // const showLoadingScreen = areStoriesLoading; // Больше не нужно
  log(`Checking loading states: areStoriesLoading=${areStoriesLoading}, isContentLoading=${isContentLoading}, areTagsLoading=${areTagsLoading}`);

  // Сообщение об ошибке, если что-то пошло не так с загрузкой контента или тегов
  const pageError = contentError || tagsError; // storiesError обрабатывается в StoriesComponent

  // Определяем, нужно ли показывать основной контент страницы
  // Это будет true, если все основные данные для HomePage загружены
  // const isPageDataLoaded = !areStoriesLoading && !isContentLoading && !areTagsLoading; // ESLint: 'isPageDataLoaded' is assigned a value but never used.

  return (
    <>
      {/* LoadingScreen удален отсюда, управляется из App.js через HTML-загрузчик */}
      {/* Убираем style visibility, будем управлять загрузкой контента отдельно */}
      <div className="home-page content-with-navbar"> 
        <div className="tg-page-header"></div>
        <div className="page-header">
          <h1>Нихао, {user?.firstName || user?.username || ''}{settings.welcome_emoji}</h1>
          <p className="app-description">{settings.welcome_text}</p>
        </div>
        
        {/* StoriesComponent теперь берет все из DataContext, ему не нужны пропсы для загрузки */}
        <div style={{ minHeight: '120px', marginBottom: '25px' }}>
          <StoriesComponent />
        </div>
        
        {/* Контейнер для тегов и кнопки подарков */}
        <div className="tags-and-gifts-container">
          {/* Отображение тегов, если они загружены и нет ошибки */}
          {!areTagsLoading && !tagsError && ( // Показываем контейнер тегов даже если их 0, чтобы кнопка подарков была на месте
            <div className="tags-filter">
              <button
                key="all"
                className={`tag-button ${selectedTag === 'all' && !isGiftModeActive ? 'active' : ''}`}
                onClick={() => handleTagClick('all')}
              >
                Все
              </button>
              {tags
                .filter(tag => !tag.isGiftTag && (tag.contentCount > 0 || tag.isSpecialSystemTag) && (tag.isActive === undefined || tag.isActive)) // Убираем isGiftTag из фильтра
                .map(tag => (
                  <button
                    key={tag._id}
                    className={`tag-button ${selectedTag === tag._id && !isGiftModeActive ? 'active' : ''}`}
                    onClick={() => handleTagClick(tag._id)}
                  >
                    {tag.name}
                  </button>
                ))}
            </div>
          )}
          {/* Кнопка Подарки */}
          {settings && settings.gifts_section_active && ( // Используем gifts_section_active
            <button 
              className={`tag-button gift-button ${isGiftModeActive ? 'active' : ''}`} 
              onClick={handleGiftButtonClick}
            >
              🎁 Подарки
            </button>
          )}
        </div>
        {tagsError && <div className="error-state" style={{marginBottom: '15px'}}><p>Ошибка загрузки тегов: {tagsError}</p></div>}
        
        {isGiftModeActive ? (
          <GiftPurchaseInterface />
        ) : (
          <div className="content-list">
            {isContentLoading && content.length === 0 ? (
              <div className="loading-state">
                <p>Загрузка материалов...</p>
            </div>
          ) : pageError ? (
            <div className="error-state">
              <p>Ошибка: {pageError}</p>
              {/* Кнопка "Повторить" теперь вызывает fetchContentData с текущим selectedTag и force=true */}
              <button onClick={() => fetchContentData(selectedTag, true)}>Повторить</button>
            </div>
          ) : content.length === 0 && selectedTag && selectedTag !== 'all' ? (
            <div className="empty-state">
              <p>Материалы с выбранным тегом не найдены.</p>
              {/* Кнопка "Показать все" теперь напрямую вызывает handleTagClick с 'all' */}
              <button onClick={() => handleTagClick('all')}>Показать все</button>
            </div>
          ) : content.length === 0 ? ( 
            // Это условие сработает, если selectedTag === 'all' и контента нет,
            // или если selectedTag не установлен (что маловероятно при инициализации 'all')
            <div className="empty-state">
              <p>Пока нет доступных материалов.</p>
            </div>
          ) : (
            <>
              {/* Показываем контент даже во время загрузки, если он есть */}
              {content.length > 0 && (
                <div className="content-cards-container">
                  {content.map(item => (
                    <ContentCard 
                      key={item._id || item.id} 
                      content={item} 
                      isPurchased={userPurchases.includes(item._id || item.id)}
                      // Удаляем onGift и isGiftButtonActive, так как логика подарков теперь на отдельной "странице"
                    />
                  ))}
                </div>
              )}
              {/* Индикатор загрузки поверх контента, если идет обновление */}
              {isContentLoading && content.length > 0 && (
                <div className="loading-overlay">
                  <div className="loading-spinner"></div>
                </div>
              )}
            </>
          )}
          </div>
        )}
      </div>
    </>
  );
};

export default HomePage;
